import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Plus, PencilSimple, Trash, ToggleLeft, ToggleRight } from "@phosphor-icons/react";

const EMPTY = {
  name: "",
  designation: "",
  phone: "",
  email: "",
  notes: "",
  active: true,
};

function EvaluatorForm({ editing, onSaved, onCancel }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setForm(editing ? { ...EMPTY, ...editing } : EMPTY);
  }, [editing]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Name is required");
    setSaving(true);
    try {
      if (editing) await api.put(`/evaluators/${editing.id}`, form);
      else await api.post("/evaluators", form);
      toast.success(editing ? "Updated" : "Created");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };
  return (
    <form
      onSubmit={submit}
      data-testid="evaluator-form"
      className="bg-canvas-card border border-canvas-line p-4 space-y-3"
    >
      <div className="grid grid-cols-2 gap-3">
        <input
          required
          value={form.name}
          onChange={(e) => set("name", e.target.value)}
          placeholder="Evaluator name *"
          data-testid="evaluator-name"
          className="px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
        />
        <input
          value={form.designation}
          onChange={(e) => set("designation", e.target.value)}
          placeholder="Designation"
          data-testid="evaluator-designation"
          className="px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <input
          value={form.phone}
          onChange={(e) => set("phone", e.target.value)}
          placeholder="Phone"
          data-testid="evaluator-phone"
          className="px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
        />
        <input
          value={form.email}
          onChange={(e) => set("email", e.target.value)}
          placeholder="Email"
          data-testid="evaluator-email"
          className="px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
        />
      </div>
      <div className="flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-3 py-1.5 border border-canvas-line text-xs hover:border-ink"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={saving}
          data-testid="evaluator-save"
          className="px-3 py-1.5 bg-ink text-white text-xs font-semibold hover:bg-brand"
        >
          {saving ? "Saving…" : editing ? "Update" : "Add Evaluator"}
        </button>
      </div>
    </form>
  );
}

export default function EvaluatorsPage() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = () =>
    api
      .get("/evaluators")
      .then((r) => setItems(r.data || []))
      .catch(() => setItems([]));
  useEffect(() => {
    load();
  }, []);

  const remove = async (e) => {
    if (!window.confirm(`Soft-delete ${e.name}?`)) return;
    try {
      await api.delete(`/evaluators/${e.id}`);
      toast.success("Deactivated");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  const toggle = async (e) => {
    try {
      await api.put(`/evaluators/${e.id}`, { ...e, active: !e.active });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="evaluators-page">
      <PageHeader
        eyebrow="05 / Masters"
        title="Vehicle Evaluators"
        subtitle="Authorised valuers for trade-in exchanges."
        actions={
          <button
            onClick={() => {
              setEditing(null);
              setShowForm(true);
            }}
            data-testid="add-evaluator-button"
            className="bg-ink text-white px-4 py-2.5 text-sm font-semibold hover:bg-brand flex items-center gap-2"
          >
            <Plus size={14} weight="bold" /> Add Evaluator
          </button>
        }
      />
      <div className="px-8 py-6 space-y-4">
        {showForm && (
          <EvaluatorForm
            editing={editing}
            onSaved={() => {
              setShowForm(false);
              load();
            }}
            onCancel={() => setShowForm(false)}
          />
        )}
        <div className="bg-canvas-card border border-canvas-line overflow-hidden">
          <table className="w-full text-sm" data-testid="evaluators-table">
            <thead className="border-b border-canvas-line bg-canvas-panel">
              <tr>
                {["Name", "Designation", "Phone", "Email", "Active", ""].map((h) => (
                  <th
                    key={h}
                    className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-10 text-center font-mono text-xs text-ink-subtle"
                  >
                    No evaluators yet.
                  </td>
                </tr>
              )}
              {items.map((ev) => (
                <tr
                  key={ev.id}
                  data-testid={`evaluator-row-${ev.id}`}
                  className="border-b border-canvas-line last:border-b-0 hover:bg-canvas-panel"
                >
                  <td className="px-4 py-3 font-semibold text-ink">{ev.name}</td>
                  <td className="px-4 py-3">{ev.designation || "—"}</td>
                  <td className="px-4 py-3 font-mono text-xs">{ev.phone || "—"}</td>
                  <td className="px-4 py-3 font-mono text-xs">{ev.email || "—"}</td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => toggle(ev)}
                      data-testid={`evaluator-toggle-${ev.id}`}
                      className="text-ink-muted hover:text-ink"
                    >
                      {ev.active ? (
                        <ToggleRight size={18} weight="bold" className="text-[#00B341]" />
                      ) : (
                        <ToggleLeft size={18} weight="bold" />
                      )}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      <button
                        onClick={() => {
                          setEditing(ev);
                          setShowForm(true);
                        }}
                        data-testid={`evaluator-edit-${ev.id}`}
                        className="p-1.5 border border-canvas-line hover:border-ink"
                      >
                        <PencilSimple size={13} weight="bold" />
                      </button>
                      <button
                        onClick={() => remove(ev)}
                        data-testid={`evaluator-delete-${ev.id}`}
                        className="p-1.5 border border-canvas-line hover:border-destructive hover:text-destructive"
                      >
                        <Trash size={13} weight="bold" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
